/**
 * Inline editor for everything a person can set on a task.
 *
 * Expands in place rather than opening a modal: the row you are editing stays visible in
 * the list it belongs to, there is no focus trap to get wrong, and Escape does the obvious
 * thing.
 *
 * Only changed fields are sent, so `PATCH` semantics stay honest — an editor that PUTs the
 * whole object silently overwrites anything another client changed while it was open.
 */

import { useEffect, useRef, useState } from "react";
import { api, type CaptureTier, type Area, type Project, type RecurrenceRequest, type Task } from "../api";
import { describeRefined, refineTaskInPlace, type AiRefined } from "../aiRefine";
import { ITEM_EDITED_EVENT } from "../editPopupEvents";
import { AiReasoningDisclosure } from "./AiReasoningDisclosure";
import { InboxAiRetryButton } from "./InboxAiRetryButton";
import { DestinationPicker, DetailFields, EnergyPicker, FlagButtons } from "./TaskFields";
import { type TaskFieldValues } from "./taskFieldValues";
import { buildRRuleString, parseRecurrence, todayLocalDate } from "./recurrence";

export type TaskPatch = Partial<
  Pick<
    Task,
    | "title"
    | "notes"
    | "area_id"
    | "project_id"
    | "energy"
    | "important"
    | "starred"
    | "estimate_min"
    | "due"
    | "snoozed_until"
  >
>;

function fieldValuesOf(task: Task): TaskFieldValues {
  return {
    destination: task.project_id ? `project:${task.project_id}` : `area:${task.area_id}`,
    energy: task.energy,
    important: task.important,
    starred: task.starred,
    due: task.due ?? "",
    snoozed: task.snoozed_until ?? "",
    estimate: task.estimate_min?.toString() ?? "",
    recurrence: parseRecurrence(task.rrule, task.recur_anchor),
    notes: task.notes ?? "",
  };
}

export function TaskEditor({
  task: initial,
  areas,
  projects,
  onSave,
  onCancel,
  onDelete,
}: {
  task: Task;
  areas: Area[];
  projects: Project[];
  /**
   * `recurrence` is `undefined` when nothing about it changed, `null` to clear it, or a
   * request to set it — a separate write from `patch` because recurrence lives behind its
   * own endpoint (§10 `POST /api/tasks/{id}/recurrence`), not the general task PATCH.
   */
  onSave: (patch: TaskPatch, recurrence?: RecurrenceRequest) => Promise<void>;
  onCancel: () => void;
  onDelete?: (task: Task) => Promise<void>;
}) {
  // §8.6: a subtask's area/project follow its parent and can't diverge, it can never be
  // starred, and it can never be recurring — offering those controls here would just be
  // three ways to fail the save.
  const subtask = initial.parent_id !== null;
  // The task as last saved. It moves when the AI bulb rewrites the task while this form is open,
  // so a Save afterwards sends only what differs from what the AI left.
  const [task, setTask] = useState(initial);
  const [title, setTitle] = useState(initial.title);
  const [values, setValues] = useState<TaskFieldValues>(() => fieldValuesOf(initial));
  const [saving, setSaving] = useState(false);
  // The person has changed the form since the AI last answered: an answer still showing gives the
  // buttons back (`InboxAiRetryButton`'s `keepOpen`).
  const [touched, setTouched] = useState(false);
  const [aiRunning, setAiRunning] = useState(false);
  // The page behind the editor refetches once the AI's change is done being looked at: a refetch
  // under an open editor can move the row, and take the editor with it.
  const refined = useRef(false);
  const announce = () => {
    if (!refined.current) return;
    refined.current = false;
    window.dispatchEvent(new Event(ITEM_EDITED_EVENT));
  };
  useEffect(() => announce, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch: Partial<TaskFieldValues>) => {
    setTouched(true);
    setValues((current) => ({ ...current, ...patch }));
  };

  /** What the form holds that differs from the saved task: the PATCH and the recurrence write. */
  function changes(): { patch: TaskPatch; recurrence: RecurrenceRequest | undefined } {
    const patch: TaskPatch = {};

    if (title.trim() && title.trim() !== task.title) patch.title = title.trim();
    if ((values.notes || null) !== task.notes) patch.notes = values.notes || null;
    if (values.energy !== task.energy) patch.energy = values.energy;
    if (values.important !== task.important) patch.important = values.important;
    if (values.starred !== task.starred) patch.starred = values.starred;
    if ((values.due || null) !== task.due) patch.due = values.due || null;
    if ((values.snoozed || null) !== task.snoozed_until) {
      patch.snoozed_until = values.snoozed || null;
    }

    const estimate = values.estimate.trim() ? Number(values.estimate) : null;
    if (estimate !== task.estimate_min) patch.estimate_min = estimate;

    let recurrence: RecurrenceRequest | undefined;
    if (!values.recurrence.enabled) {
      if (task.rrule !== null) recurrence = { rrule: null, recur_anchor: null, recur_start: null };
    } else {
      // The Due field doubles as the rule's start date (DECISIONS.md D83); an empty one
      // falls back to today rather than blocking the save on a field this form does not
      // otherwise require.
      const recurStart = values.due || todayLocalDate();
      const rrule = buildRRuleString(values.recurrence);
      if (
        rrule !== task.rrule ||
        values.recurrence.anchor !== task.recur_anchor ||
        recurStart !== task.recur_start
      ) {
        recurrence = { rrule, recur_anchor: values.recurrence.anchor, recur_start: recurStart };
      }
    }

    if (values.destination.startsWith("project:")) {
      const projectId = values.destination.slice(8);
      if (projectId !== task.project_id) patch.project_id = projectId;
    } else {
      const areaId = values.destination.slice(5);
      // Moving out of a project has to clear it, or the task keeps a project belonging to
      // a different area and §4.1's "exactly one area" stops being true.
      if (task.project_id) patch.project_id = null;
      if (areaId !== task.area_id) patch.area_id = areaId;
    }

    return { patch, recurrence };
  }

  /**
   * The AI bulb: the form's changed values go to the AI, and the answer is written onto this task
   * - the same task, not a new one. Fields the AI does not touch (snooze, repeat) are saved as the
   * form holds them once it has answered. The form is left showing what the AI wrote, while the
   * bulb's answer box says what it did; a failure rejects, and the form is as it was.
   */
  async function askAi(tier: CaptureTier | null): Promise<AiRefined<Task>> {
    const { patch, recurrence } = changes();
    const { snoozed_until: snoozed, ...fields } = patch;
    setTouched(false);
    setAiRunning(true);
    try {
      const answer = await refineTaskInPlace(task.id, { ...fields, ...(tier ? { tier } : {}) });
      if (snoozed !== undefined) await api.patchTask(task.id, { snoozed_until: snoozed });
      if (recurrence !== undefined) await api.setRecurrence(task.id, recurrence);
      const saved =
        snoozed !== undefined || recurrence !== undefined
          ? await api.task(task.id).catch(() => answer.item)
          : answer.item;
      refined.current = true;
      setTask(saved);
      setTitle(saved.title);
      setValues(fieldValuesOf(saved));
      return { ...answer, item: saved };
    } finally {
      setAiRunning(false);
    }
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    const { patch, recurrence } = changes();
    if (Object.keys(patch).length === 0 && recurrence === undefined) {
      onCancel();
      return;
    }

    setSaving(true);
    try {
      await onSave(patch, recurrence);
    } finally {
      setSaving(false);
    }
  }

  return (
    <li className="task editing">
      <form
        data-ai-host=""
        onSubmit={save}
        onKeyDown={(e) => e.key === "Escape" && onCancel()}
        style={{ display: "grid", gap: "0.55rem", width: "100%" }}
      >
        <input
          className="input"
          aria-label="Title"
          value={title}
          onChange={(e) => {
            setTouched(true);
            setTitle(e.target.value);
          }}
          autoFocus
        />

        <div className="row wrap">
          {subtask ? (
            <span className="muted small">
              Area and project follow the parent task - change them there.
            </span>
          ) : (
            <DestinationPicker
              value={values.destination}
              areas={areas}
              projects={projects}
              onChange={(destination) => update({ destination })}
            />
          )}
          {/*
            Energy, important and star are one block: they are the marks you put on a task,
            and they should leave the destination picker together rather than one at a time.
            Inside it, the two flags stay paired.
          */}
          <span className="field-marks">
            <EnergyPicker value={values.energy} onChange={(energy) => update({ energy })} />
            <FlagButtons
              important={values.important}
              starred={values.starred}
              onImportant={(important) => update({ important })}
              onStarred={(starred) => update({ starred })}
              hideStar={subtask}
            />
          </span>
        </div>

        <DetailFields values={values} onChange={update} hideRecurrence={subtask} />

        <AiReasoningDisclosure reasoning={task.ai_reasoning} />

        <div className="row wrap">
          {onDelete && (
            <button
              type="button"
              className="btn ghost small danger inbox-fold"
              onClick={() => {
                // Deleting is for things that should not exist — a test entry, a typo.
                // Dropping is for things you are not going to do. The confirm has to make
                // that difference obvious, because only one of them is reversible.
                if (
                  window.confirm(
                    `Permanently delete "${task.title}"?\n\n` +
                      "This cannot be undone. If you simply are not going to do it, use " +
                      "the bin on the row instead - that drops it and keeps it.\n\n" +
                      "Anything it already did stays in your history either way.",
                  )
                ) {
                  void onDelete(task);
                }
              }}
            >
              Delete permanently
            </button>
          )}
          <span className="ai-spacer" style={{ flex: 1 }} />
          <button type="button" className="btn ghost small inbox-fold" onClick={onCancel}>
            Cancel
          </button>
          <InboxAiRetryButton<AiRefined<Task>>
            surface="task-form"
            label="Tidy up with AI"
            busyLabel="Asking the AI"
            doneLabel="Close - the AI's changes are saved"
            disabled={saving || !title.trim()}
            keepOpen={touched}
            describe={describeRefined}
            onRetry={askAi}
            onSettled={(result) => {
              // Only a held answer being cleared closes the editor; a failure cleared leaves it.
              if (!result) return;
              announce();
              onCancel();
            }}
            leaveOnClear
          />
          <button className="btn primary small inbox-fold" type="submit" disabled={saving || aiRunning}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </li>
  );
}
